import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import schoolServiceAdminInstance from '../../services/SchoolServiceAdmin';

const HolidaysServices = ({ data }) => {
  const navigate = useNavigate();

  const tableContainerRef = useRef(null);

  // State instead of refs for better control
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [loading, setLoading] = useState(true);
  const [allHolidays, setAllHolidays] = useState(null);

  // Check if user is a SUPER_ADMIN
  const isSuperAdmin = data.roles?.some(
    (role) => role.roleCode === 'SUPER_ADMIN'
  );

  useEffect(() => {
    if (tableContainerRef.current && allHolidays?.length > 0) {
      const el = tableContainerRef.current;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [allHolidays]);

  useEffect(() => {
    if (!data || !isSuperAdmin) {
      localStorage.clear();
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [holidays] = await Promise.all([
          schoolServiceAdminInstance.getAllHolidays(),
        ]);

        setAllHolidays(
          holidays.data.sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          )
        );
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [data, isSuperAdmin, navigate]);

  const createNewHolidayDate = async () => {
    if (!dateFrom) {
      alert('Please select at least the "From" date.');
      return;
    }

    const start = new Date(dateFrom);
    const end = dateTo ? new Date(dateTo) : start;

    if (start > end) {
      alert('"From" date cannot be after "To" date.');
      return;
    }

    setLoading(true);
    try {
      const datesToCreate = [];
      for (
        let dt = new Date(start);
        dt <= end;
        dt.setDate(dt.getDate() + 1)
      ) {
        datesToCreate.push(new Date(dt).toISOString().split('T')[0]);
      }

      await Promise.all(
        datesToCreate.map((date) =>
          schoolServiceAdminInstance.createNewHoliday(date)
        )
      );

      refreshAllHolidays();
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  /*const createNewHolidayDate = async () => {
    try {
      // Check if a date is provided
      if (!dateRef.current.value) {
        alert('Please select a date before proceeding.');
        return; // Exit the function if no date is given
      }

      // Proceed to create a new holiday
      await schoolServiceAdminInstance.createNewHoliday(
        dateRef.current.value
      );
      refreshAllHolidays();
    } catch (error) {
      console.log(error);
    }
  };*/

  const refreshAllHolidays = async () => {
    setLoading(true);
    try {
      const response2 =
        await schoolServiceAdminInstance.getAllHolidays();
      setAllHolidays(
        response2.data.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        )
      );
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  return (
    <div className="container mx-auto my-8">
      <h2 className="inline-flex items-center justify-center w-full md:text-2xl text-2xl text-center py-2 font-semibold text-cyan-900 ">
        Add or Remove Holidays
      </h2>

      <div className="flex flex-col items-center justify-center w-full gap-2">
        <div className="w-full max-w-xs">
          <label className="block text-md font-semibold">From:</label>
          <input
            className="w-full m-1 py-2 px-3 block border-2 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 hover:border-black transition-all duration-300"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="w-full max-w-xs">
          <label className="block text-md font-semibold">
            To (optional):
          </label>
          <input
            className="w-full m-1 py-2 px-3 block border-2 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 hover:border-black transition-all duration-300"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <div className="inline-flex items-center justify-center w-full my-4">
        <button
          onClick={createNewHolidayDate}
          className="rounded bg-green-700 text-white px-6 py-3 font-semibold hover:bg-green-800"
        >
          + Add New Holiday
        </button>
      </div>

      {!loading && (
        <div className="flex justify-center">
          <div
            ref={tableContainerRef}
            className="overflow-y-auto max-h-[500px] rounded border w-full max-w-2xl"
          >
            <table className="min-w-full border-separate">
              <thead className="bg-gray-800 text-white sticky top-0">
                <tr>
                  <th className="py-2 px-4">ID</th>
                  <th className="py-2 px-4">Date</th>
                  <th className="py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allHolidays?.map((holiday) => (
                  <tr
                    className="border-b text-center"
                    key={holiday.id}
                  >
                    <td className="py-2 px-4">{holiday.id}</td>
                    <td className="py-2 px-4">{holiday.date}</td>
                    <td className="py-2 px-4">
                      <button
                        onClick={async () => {
                          await schoolServiceAdminInstance.deleteHoliday(
                            holiday.id
                          );
                          refreshAllHolidays();
                        }}
                        className="rounded bg-red-700 text-white px-6 py-2 font-semibold hover:bg-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />
      <div className="mx-auto text-center">
        <button
          onClick={() => {
            navigate('/schoolsuperadmin');
          }}
          className="rounded text-white px-6 py-2 font-semibold bg-cyan-700 hover:bg-cyan-800"
        >
          Back To Dashboard
        </button>
      </div>
    </div>
  );
};

export default HolidaysServices;
